/**
 * Auto-detect for the hex, ASCII-code and binary converters. Text such as "cafe",
 * "2024" or "101" is just as likely to be a word or a number as encoded data, so
 * detection only says "encoded" for shapes that are unambiguous; everything else is
 * treated as text, and the Direction buttons force the other reading.
 */

const SEPARATOR = /[\s,]+/;
const HEX_BYTE = /^[0-9a-fA-F]{2}$/;
const PREFIXED_HEX = /^0[xX](?:[0-9a-fA-F]{2})+$/;
const BINARY_BYTE = /^[01]{8}$/;
const DECIMAL = /^\d{1,3}$/;
const MAX_LATIN1 = 255;

function tokens(text: string): string[] {
  const trimmed = text.trim();
  return trimmed === "" ? [] : trimmed.split(SEPARATOR);
}

/**
 * Hex bytes: two or more two-digit groups ("48 65 6c"), or every group written with a
 * 0x prefix ("0x48 0x65", "0x4865"). A bare run such as "cafe" is text.
 */
export function looksLikeHex(text: string): boolean {
  const parts = tokens(text);
  if (parts.length === 0) {
    return false;
  }
  if (parts.every((part) => PREFIXED_HEX.test(part))) {
    return true;
  }
  return parts.length >= 2 && parts.every((part) => HEX_BYTE.test(part));
}

/** Character codes: two or more numbers separated by spaces or commas, each 0-255. */
export function looksLikeCharacterCodes(text: string): boolean {
  const parts = tokens(text);
  return (
    parts.length >= 2 &&
    parts.every((part) => DECIMAL.test(part) && Number(part) <= MAX_LATIN1)
  );
}

/** Binary: every whitespace-separated group is exactly eight 0/1 digits. */
export function looksLikeBinary(text: string): boolean {
  const parts = tokens(text);
  return parts.length > 0 && parts.every((part) => BINARY_BYTE.test(part));
}
